/**
 * Floating Action Button (FAB)
 * Task 5.1: Comment Mode Toggle
 *
 * Collapsible horizontal toolbar design inspired by PreviewToolbar.
 * Uses PDS design tokens for consistent Pantheon branding.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { LightningBolt } from './icons/LightningBolt';
import { tokens } from '../styles/tokens';

interface FABProps {
  isCommentModeActive: boolean;
  onToggleCommentMode: () => void;
  onToggleSidebar: () => void;
  /** Number of unread comments to display in badge */
  commentCount: number;
}

export const FAB: React.FC<FABProps> = ({
  isCommentModeActive,
  onToggleCommentMode,
  onToggleSidebar,
  commentCount,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [commentsTooltipVisible, setCommentsTooltipVisible] = useState(false);
  const lastCKeyPressRef = useRef<number>(0);

  const handleCommentsClick = useCallback(() => {
    if (isCommentModeActive) {
      // Exit comment mode
      onToggleCommentMode();
    } else {
      // Open sidebar and enable comment mode (same as /+c keyboard shortcut)
      onToggleSidebar();
      onToggleCommentMode();
    }
  }, [isCommentModeActive, onToggleCommentMode, onToggleSidebar]);

  const handleActivateCommentMode = useCallback(() => {
    if (!isCommentModeActive) {
      onToggleCommentMode();
    }
  }, [isCommentModeActive, onToggleCommentMode]);

  const handleCKeyPress = useCallback(() => {
    const now = Date.now();
    const timeSinceLastPress = now - lastCKeyPressRef.current;

    // Double-tap detection: if "c" pressed within 500ms, open sidebar
    if (timeSinceLastPress < 500 && timeSinceLastPress > 0) {
      onToggleSidebar();
      lastCKeyPressRef.current = 0; // Reset to prevent triple-tap
    } else {
      // Single press: activate comment mode
      handleActivateCommentMode();
      lastCKeyPressRef.current = now;
    }
  }, [onToggleSidebar, handleActivateCommentMode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Get the actual event target, even if it's inside shadow DOM
      // composedPath() returns the event path before shadow DOM retargeting
      const actualTarget = (e.composedPath()[0] as HTMLElement) || (e.target as HTMLElement);

      // Check if user is typing in any text input context
      const isTyping =
        actualTarget.tagName === 'INPUT' ||
        actualTarget.tagName === 'TEXTAREA' ||
        actualTarget.tagName === 'SELECT' ||
        actualTarget.isContentEditable ||
        actualTarget.getAttribute('role') === 'textbox';

      // "/" key - expand toolbar while held down
      if (e.key === '/' && !e.repeat && !isTyping) {
        e.preventDefault();
        setCollapsed(false);
        return;
      }

      // "c" key - activate comment mode (single) or open sidebar (double-tap)
      if ((e.key === 'c' || e.key === 'C') && !collapsed && !isTyping) {
        e.preventDefault();
        handleCKeyPress();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Collapse toolbar when "/" is released
      if (e.key === '/') {
        setCollapsed(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [collapsed, handleCKeyPress]);

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleMouseEnter = () => {
    setCollapsed(false);
  };

  const handleMouseLeave = () => {
    setCollapsed(true);
  };

  return (
    <div
      data-multidev-sdk="true"
      style={{
        position: 'fixed',
        bottom: tokens.spacing.xl,
        right: tokens.spacing.xl,
        zIndex: 1000000,
        pointerEvents: 'auto',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Comments Tooltip - rendered outside overflow container */}
      {commentsTooltipVisible && !collapsed && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '32px',
            transform: 'translateX(-50%)',
            marginBottom: tokens.spacing.xs,
            backgroundColor: tokens.colors.gray800,
            color: tokens.colors.white,
            padding: `${tokens.spacing['2xs']} ${tokens.spacing.xs}`,
            borderRadius: tokens.borders.radiusInput,
            fontSize: tokens.typography.fontSize.xs,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: tokens.elevation.raised,
          }}
        >
          Comments
        </div>
      )}

      {/* Unified toolbar container */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: tokens.colors.black,
          boxShadow: tokens.elevation.overlay,
          border: `${tokens.borders.widthDefault} solid ${tokens.colors.gray800}`,
          borderRadius: tokens.borders.radiusContainer,
          height: '48px',
          transition: `all 300ms ${tokens.animation.timing}`,
        }}
      >
        {/* Sliding content panel */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing.xs,
            transition: 'all 300ms ease-out',
            maxWidth: collapsed ? '0' : '12rem',
            opacity: collapsed ? 0 : 1,
            paddingLeft: collapsed ? '0' : tokens.spacing.m,
            paddingRight: collapsed ? '0' : tokens.spacing.m,
            overflow: collapsed ? 'hidden' : 'visible',
          }}
          aria-hidden={collapsed}
        >
          {/* Comments Button */}
          <button
            onClick={handleCommentsClick}
            aria-label={isCommentModeActive ? 'Exit comment mode' : 'Open comments'}
            style={{
              height: '32px',
              width: '32px',
              borderRadius: tokens.borders.radiusInput,
              border: 'none',
              backgroundColor: isCommentModeActive ? tokens.colors.white : 'transparent',
              color: isCommentModeActive ? tokens.colors.black : tokens.colors.white,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              transition: `all ${tokens.animation.duration} ${tokens.animation.timing}`,
            }}
            onMouseEnter={(e) => {
              if (!isCommentModeActive) {
                e.currentTarget.style.backgroundColor = tokens.colors.gray800;
              }
              setCommentsTooltipVisible(true);
            }}
            onMouseLeave={(e) => {
              if (!isCommentModeActive) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
              setCommentsTooltipVisible(false);
            }}
          >
            <MessageSquare style={{ width: '16px', height: '16px' }} />
            {/* Comment Count Badge - shown when expanded */}
            {!collapsed && commentCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  backgroundColor: tokens.colors.criticalFg,
                  color: tokens.colors.textReverse,
                  borderRadius: tokens.spacing.xs,
                  minWidth: tokens.spacing.l,
                  height: tokens.spacing.l,
                  padding: `0 ${tokens.spacing['4xs']}`,
                  boxSizing: 'border-box',
                  fontSize: tokens.typography.fontSize.xs,
                  fontWeight: tokens.typography.fontWeight.bold,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: tokens.elevation.raised,
                }}
              >
                {commentCount > 99 ? '99+' : commentCount}
              </span>
            )}
          </button>
        </div>

        {/* Persistent expand/close button */}
        <div style={{ flexShrink: 0 }}>
          <button
            onClick={handleToggleCollapse}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand toolbar' : 'Collapse toolbar'}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: tokens.borders.radiusContainer,
              height: '48px',
              width: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              position: 'relative',
              transition: `all 300ms ${tokens.animation.timing}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colors.gray800;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {/* Lightning bolt icon - visible when collapsed */}
            <div
              style={{
                position: 'absolute',
                transition: 'opacity 300ms',
                opacity: collapsed ? 1 : 0,
                height: '80%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LightningBolt style={{ height: '100%', width: 'auto' }} />
            </div>

            {/* Close icon - visible when expanded */}
            <X
              style={{
                width: '16px',
                height: '16px',
                position: 'absolute',
                color: tokens.colors.white,
                transition: 'opacity 300ms',
                opacity: collapsed ? 0 : 1,
              }}
            />

            {/* Comment Count Badge */}
            {collapsed && commentCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: tokens.spacing['5xs'],
                  right: tokens.spacing['5xs'],
                  backgroundColor: tokens.colors.criticalFg,
                  color: tokens.colors.textReverse,
                  borderRadius: tokens.spacing.xs,
                  minWidth: tokens.spacing.l,
                  height: tokens.spacing.l,
                  padding: `0 ${tokens.spacing['4xs']}`,
                  boxSizing: 'border-box',
                  fontSize: tokens.typography.fontSize.xs,
                  fontWeight: tokens.typography.fontWeight.bold,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: tokens.elevation.raised,
                }}
              >
                {commentCount > 99 ? '99+' : commentCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
